import { Badge, Button, Chip, DataTable, type DataTableColumn } from '@zarya/ui';
import { t } from '@app/i18n';
import type { AccessControlState, IncomingRow } from './AccessControlModel';
import s from './AccessControl.module.css';

export interface IncomingTabProps {
  estado: AccessControlState;
  /** Troca a identidade selecionada — o A2A de saída se edita do lado do chamador. */
  onEditarA2ADe: (identityId: string) => void;
}

/**
 * Aba **Incoming** (FR-075): quem pode invocar esta identidade.
 *
 * É **derivada** dos contratos dos chamadores e por isso somente leitura: para
 * mudar, edita-se o A2A de saída da outra identidade — o botão leva até lá.
 */
export function IncomingTab({ estado, onEditarA2ADe }: IncomingTabProps) {
  const nome = estado.identity.slug ?? t('identityAccess.identityFallback');

  const columns: DataTableColumn<IncomingRow>[] = [
    {
      key: 'identidade',
      header: t('identityAccess.incoming.col.identity'),
      cell: (linha) => <b>{linha.slug}</b>,
    },
    {
      key: 'tipo',
      header: t('identityAccess.incoming.col.type'),
      cell: (linha) => <Chip>{linha.role ?? t('identityAccess.noRole')}</Chip>,
    },
    {
      key: 'pode',
      header: t('identityAccess.incoming.col.canInvoke', { name: nome }),
      cell: (linha) =>
        linha.can_invoke ? (
          <Badge tone="success">{t('identityAccess.incoming.yes')}</Badge>
        ) : (
          <Badge tone="neutral">{t('identityAccess.incoming.no')}</Badge>
        ),
    },
    {
      key: 'acao',
      header: '',
      numeric: true,
      cell: (linha) => (
        <Button
          variant="out"
          size="sm"
          onClick={() => linha.identity_id && onEditarA2ADe(linha.identity_id)}
        >
          {t('identityAccess.incoming.edit')}
        </Button>
      ),
    },
  ];

  // Fase 2: quem é chamado é o RECURSO AGENTE ligado à identidade. Sem nenhum, ela chama e ninguém a chama — e o
  // vazio diz isso, em vez de «nenhum contrato invoca», que sugeriria um contrato a mudar.
  const receives = estado.receives_a2a;
  return (
    <DataTable
      label={t('identityAccess.incoming.label', { name: nome })}
      columns={columns}
      rows={receives ? estado.incoming : []}
      rowKey={(linha) => linha.identity_id ?? linha.slug ?? ''}
      filters={<span className={s.nota}>{t('identityAccess.incoming.note')}</span>}
      emptyIcon="robot"
      emptyTitle={t(receives ? 'identityAccess.incoming.empty.title' : 'identityAccess.incoming.notReceiving.title')}
      emptyMessage={
        receives
          ? t('identityAccess.incoming.empty.message', { name: nome })
          : t('identityAccess.incoming.notReceiving.message')
      }
    />
  );
}
